import { transformWon } from "@repo/utils/src/number";
import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { URLS } from "@const/urls";

export type TMembership = "EMPLOYEE" | "FRIENDS" | "WHITE" | "GREEN" | "SILVER" | "GOLD" | "PLATINUM" | "BLACK";

export interface IMembershipInfo {
  startRange: number;
  endRange: number;
  kakaoDiscount: string | null;
  discount: string | null;
  coupon: string | null;
  textColor: string;
  bgColor: string;
}

export const MEMBERSHIP_INFO: Record<TMembership, IMembershipInfo> = {
  EMPLOYEE: {
    startRange: 0,
    endRange: 0,
    kakaoDiscount: null,
    discount: "30",
    coupon: null,
    textColor: "text-black",
    bgColor: "bg-tuneGrey-100",
  },
  FRIENDS: {
    startRange: 0,
    endRange: 0,
    kakaoDiscount: null,
    discount: "0",
    coupon: null,
    textColor: "text-black",
    bgColor: "bg-tuneGrey-100",
  },
  WHITE: {
    startRange: 0,
    endRange: 199999,
    kakaoDiscount: "10",
    discount: null,
    coupon: null,
    textColor: "text-black",
    bgColor: "bg-tuneGrey-100",
  },
  GREEN: {
    startRange: 200000,
    endRange: 499999,
    kakaoDiscount: null,
    discount: "1",
    coupon: "10",
    textColor: "text-black",
    bgColor: "bg-lime-200",
  },
  SILVER: {
    startRange: 500000,
    endRange: 999999,
    kakaoDiscount: null,
    discount: "3",
    coupon: "10",
    textColor: "text-black",
    bgColor: "bg-gray-300",
  },
  GOLD: {
    startRange: 1000000,
    endRange: 4999999,
    kakaoDiscount: null,
    discount: "5",
    coupon: "10",
    textColor: "text-white",
    bgColor: "bg-yellow-700",
  },
  PLATINUM: {
    startRange: 5000000,
    endRange: 9999999,
    kakaoDiscount: null,
    discount: "7",
    coupon: "10",
    textColor: "text-white",
    bgColor: "bg-slate-500",
  },
  BLACK: {
    startRange: 10000000,
    endRange: 10000000,
    kakaoDiscount: null,
    discount: "10",
    coupon: "10",
    textColor: "text-white",
    bgColor: "bg-zinc-800",
  },
};

interface IMembershipCard {
  membership: TMembership;
  nextMonthMembership?: TMembership;
  tag: "li" | "div";
}

function MembershipCard({ membership, nextMonthMembership, tag = "div" }: IMembershipCard) {
  const { t } = useTranslation();
  const Tag = tag;

  // 직원 / 지인 인지 판단
  const isEmployee = useMemo(() => membership === "EMPLOYEE" || membership === "FRIENDS", [membership]);

  const nextMonth = useMemo(() => {
    return new Date().getMonth() === 11 ? 1 : new Date().getMonth() + 2;
  }, []);

  const data = MEMBERSHIP_INFO[membership];
  if (!data) return null;

  return (
    <Tag
      className={`pc:min-h-[118px] pc:flex-row pc:text-left flex min-h-[161px] flex-col gap-5 rounded-sm p-5 ${data.bgColor} ${data.textColor}`}
    >
      <div className="flex w-full flex-col gap-2.5">
        <strong className="font-tune w-full text-3xl leading-tight uppercase">{membership}</strong>
        {nextMonthMembership && !isEmployee ? (
          <span className="text-md font-regular">
            {t("membership_program.membership_system.next_month_membership", {
              month: nextMonth,
              membership: nextMonthMembership,
            })}
          </span>
        ) : null}
      </div>
      <div className="pc:gap-5 flex w-full flex-col gap-2.5">
        <div className="flex flex-col">
          {data.kakaoDiscount ? (
            <span>
              <Trans
                components={{
                  channel: <Link className="underline" target="_blank" to={URLS.KAKAO_PLUS} />,
                }}
                i18nKey="membership_program.membership_system.kakao_discount"
              />
            </span>
          ) : null}
          {data.discount ? (
            <span>
              {membership === "EMPLOYEE" && t("membership_program.membership_system.employee_discount")}
              {membership === "FRIENDS" && t("membership_program.membership_system.employee_discount")}
              {!isEmployee && t("membership_program.membership_system.discount", { amount: data.discount })}
            </span>
          ) : null}
          {data.coupon ? (
            <span>{t("membership_program.membership_system.coupon", { amount: data.coupon })}</span>
          ) : null}
        </div>
        {!isEmployee && (
          <span>
            {t("membership_program.membership_system.amount_range", {
              start: `${transformWon(data.startRange)}원`,
              end: data.endRange === data.startRange ? "" : `${transformWon(data.endRange)}원`,
            })}
          </span>
        )}
      </div>
    </Tag>
  );
}

export default MembershipCard;
